import * as React from 'react';
import { Client, Episode, ContractHistoryEntry } from '../../types';
import {
  Typography,
  Box,
} from '@mui/material';
import ContractStatus from './ContractStatus';

interface CounsellingInformationProps {
  client: Client;
  episodes: Episode[];
  episodesLoading: boolean;
  episodesError: string | null;
  createEpisode: (episodeData: Partial<Episode>) => Promise<Episode>;
}

export const CounsellingInformation: React.FC<CounsellingInformationProps> = ({
  client,
  episodes,
  episodesLoading,
  episodesError,
  createEpisode,
}) => {
  // Phase 2C, Stage 5: prefer the server-computed severity labels when
  // present (every role now receives them); the score-based fallback
  // covers any response shape that predates this field.
  const getDepressionSeverity = React.useCallback((client: Client): string => {
    if (client.depressionSeverity !== undefined) return client.depressionSeverity;
    const score = client.phq9Score;
    if (score === undefined) return '';
    if (score >= 20) return 'Severe';
    if (score >= 15) return 'Moderately Severe';
    if (score >= 10) return 'Moderate';
    if (score >= 5) return 'Mild';
    return 'None';
  }, []);

  const getAnxietySeverity = React.useCallback((client: Client): string => {
    if (client.anxietySeverity !== undefined) return client.anxietySeverity;
    const score = client.gad7Score;
    if (score === undefined) return 'Not assessed';
    if (score === 0) return 'None';
    if (score >= 15) return 'Severe';
    if (score >= 10) return 'Moderate';
    if (score >= 5) return 'Mild';
    return 'None';
  }, []);

  // Phase 2C, Stage 5: prefer the server-computed `riskLevel` (present on
  // every role's response now, including counsellors) over re-deriving it
  // client-side, so a future policy change to the risk model only needs
  // to change server/src/utils/clinicalSummary.ts.
  const getRiskLevel = React.useCallback((
    client: Client
  ): { level: 'low' | 'medium' | 'high' | 'unassessed', color: string } => {
    const COLORS: Record<'low' | 'medium' | 'high' | 'unassessed', string> = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-red-600',
      unassessed: 'text-gray-500'
    };

    // Never administered is not the same as assessed low - existing-client
    // onboarding completes assessments without PHQ-9/GAD-7 questionnaires.
    const scoresRecorded = client.scoresRecorded ??
      ((client.assessment?.phq9?.length ?? 0) > 0 && (client.assessment?.gad7?.length ?? 0) > 0);
    if (scoresRecorded === false) {
      return { level: 'unassessed', color: COLORS.unassessed };
    }

    if (client.riskLevel) {
      return { level: client.riskLevel, color: COLORS[client.riskLevel] };
    }

    // Fallback for any response shape that doesn't yet include the
    // server-computed field.
    const { phq9Score, gad7Score, hasPlan } = client;
    if (hasPlan === 'yes') {
      return { level: 'high', color: COLORS.high };
    }
    if (!phq9Score && !gad7Score) return { level: 'low', color: COLORS.low };
    if ((phq9Score && phq9Score >= 20) || (gad7Score && gad7Score >= 15)) {
      return { level: 'high', color: COLORS.high };
    }
    if ((phq9Score && phq9Score >= 15) || (gad7Score && gad7Score >= 10)) {
      return { level: 'medium', color: COLORS.medium };
    }
    return { level: 'low', color: COLORS.low };
  }, []);

  // Phase 2C, Stage 5: counsellor-scoped API responses no longer include
  // the raw `assessment.phq9` array (only assessors/admins receive it) -
  // the server now computes these same derived values and sends them
  // directly as `hasSelfHarmThoughts`/`selfHarmThoughtsLevel` on the
  // client object (see server/src/utils/clinicalSummary.ts). Falling back
  // to the old client-side derivation keeps this working unchanged for
  // assessor/admin views, which still receive the raw array.
  const hasSelfHarmThoughts = React.useCallback((client: Client): boolean => {
    if (typeof client.hasSelfHarmThoughts === 'boolean') return client.hasSelfHarmThoughts;
    if (!client.assessment?.phq9) return false;
    return client.assessment.phq9[8] > 0;
  }, []);

  const getSelfHarmThoughtsLevel = React.useCallback((client: Client): string => {
    if (typeof client.selfHarmThoughtsLevel === 'string') return client.selfHarmThoughtsLevel;

    const selfHarmAnswerMap: { [key: string]: string } = {
      '0': 'Not at all',
      '1': 'Several days',
      '2': 'More than half the days',
      '3': 'Nearly every day'
    };
    
    if (!client.assessment?.phq9) return '';
    const score = client.assessment.phq9[8];
    return score !== undefined ? selfHarmAnswerMap[score.toString()] : '';
  }, []);

  return (
    <div className="space-y-6">
            {/* Contract Status */}
            <Box sx={{ mb: 3, p: { xs: 2, sm: 3, md: 4 }, background: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
        <div className="flex justify-between items-center mb-6">
        <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Contract Status
          </Typography>
        </div>
        <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', borderRadius: 1.5 }}>
          <ContractStatus client={client} />
        </Box>
      </Box>
      {/* Episode History (episodes of care - a new one starts each time the client recontracts) */}
      {(episodesLoading || episodesError || episodes.length > 0) && (
        <div className="glass-card-light rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 700,
                fontSize: '1.8rem',
                color: 'rgba(255, 255, 255, 0.95)',
                letterSpacing: '0.5px',
                marginBottom: 0
              }}
            >
              Episodes of Care
            </Typography>
          </div>
          {episodesLoading ? (
            <Typography className="theme-text-secondary">Loading episodes...</Typography>
          ) : episodesError ? (
            <Typography sx={{ color: '#f87171' }}>{episodesError}</Typography>
          ) : (
            <div className="space-y-4">
              {episodes.map((episode) => (
                <div key={episode._id} className="glass-card p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Typography variant="subtitle1" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                        {episode.type.charAt(0).toUpperCase() + episode.type.slice(1)} Episode
                      </Typography>
                      <Typography variant="caption" className="theme-text-secondary">
                        Started {new Date(episode.startDate).toLocaleDateString('en-GB')}
                      </Typography>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      episode.status === 'active'
                        ? 'bg-green-500/20 text-green-300'
                        : episode.status === 'completed'
                        ? 'bg-gray-500/20 text-gray-300'
                        : 'bg-orange-500/20 text-orange-300'
                    }`}>
                      {episode.status.charAt(0).toUpperCase() + episode.status.slice(1)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs theme-text-secondary block">Number of Sessions</span>
                      <span className="text-sm theme-text-primary font-medium">
                        {episode.numberOfSessions ?? 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs theme-text-secondary block">Ended</span>
                      <span className="text-sm theme-text-primary font-medium">
                        {episode.endDate ? new Date(episode.endDate).toLocaleDateString('en-GB') : 'Ongoing'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Contract History (from recontracting) */}
      {client.contractHistory && client.contractHistory.length > 0 && (
        <div className="glass-card-light rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 700,
                fontSize: '1.8rem',
                color: 'rgba(255, 255, 255, 0.95)',
                letterSpacing: '0.5px',
                marginBottom: 0
              }}
            >
              Previous Contracts
            </Typography>
            {client.recontractedAt && (
              <span className="text-sm theme-text-secondary">
                Last recontracted: {new Date(client.recontractedAt).toLocaleDateString('en-GB')}
              </span>
            )}
          </div>
          <div className="space-y-4">
            {client.contractHistory.map((contract: ContractHistoryEntry, index: number) => (
              <div key={index} className="glass-card p-4 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <Typography variant="subtitle1" className="theme-text-primary" sx={{ fontWeight: 600 }}>
                      Contract #{index + 1} — {contract.organisationDisplayName || 'Organisation'}
                    </Typography>
                    <Typography variant="caption" className="theme-text-secondary">
                      {contract.referralCategory === 'organisation' ? 'Organisation Referral' : 'Direct Referral'}
                      {contract.referralTypeDisplayName ? ` (${contract.referralTypeDisplayName})` : ''}
                    </Typography>
                  </div>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-300">
                    Archived
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-xs theme-text-secondary block">Session Rate</span>
                    <span className="text-sm theme-text-primary font-medium">
                      {contract.sessionRate !== undefined ? `£${contract.sessionRate}` : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs theme-text-secondary block">Sessions Completed</span>
                    <span className="text-sm theme-text-primary font-medium">
                      {contract.completedSessions || 0}{contract.numberOfSessions ? ` / ${contract.numberOfSessions}` : ''}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs theme-text-secondary block">PHQ-9 / GAD-7</span>
                    <span className="text-sm theme-text-primary font-medium">
                      {contract.phq9Score ?? 'N/A'} / {contract.gad7Score ?? 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs theme-text-secondary block">Period</span>
                    <span className="text-sm theme-text-primary font-medium">
                      {contract.startDate ? new Date(contract.startDate).toLocaleDateString('en-GB') : 'N/A'}
                      {' — '}
                      {contract.endDate ? new Date(contract.endDate).toLocaleDateString('en-GB') : 'N/A'}
                    </span>
                  </div>
                </div>
                {contract.reason && (
                  <div className="mt-2">
                    <span className="text-xs theme-text-secondary">Reason: </span>
                    <span className="text-xs theme-text-primary">{contract.reason}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

            {/* Risk Assessment */}
            <Box sx={{ mb: 3, p: { xs: 2, sm: 3, md: 4 }, background: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
        <div className="flex justify-between items-center mb-6">
        <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Risk Assessment
          </Typography>
        </div>
        <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', borderRadius: 1.5 }}>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="theme-text-primary" style={{ opacity: 0.7 }}>Risk Level:</span>
              {hasSelfHarmThoughts(client) ? (
                <span className="px-2 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">
                  High (Self-harm risk)
                </span>
              ) : (
                <span className={`px-2 py-1 text-sm font-semibold rounded-full ${
                  getRiskLevel(client).level === 'low' 
                    ? 'bg-green-100 text-green-800'
                    : getRiskLevel(client).level === 'medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {getRiskLevel(client).level.charAt(0).toUpperCase() + 
                   getRiskLevel(client).level.slice(1)}
                </span>
              )}
            </div>
            {hasSelfHarmThoughts(client) && (
              <>
                <div className="flex items-center justify-between">
                  <span className="theme-text-primary" style={{ opacity: 0.7 }}>Self-harm Thoughts:</span>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.9 }}>
                    {getSelfHarmThoughtsLevel(client)}
                  </span>
                </div>
                {client.planDetails && (
                  <div className="flex items-center justify-between">
                    <span className="theme-text-primary" style={{ opacity: 0.7 }}>Plan Details:</span>
                    <span className="text-sm theme-text-primary" style={{ opacity: 0.9 }}>
                      {client.planDetails}
                    </span>
                  </div>
                )}
              </>
            )}
            {client.riskToSelf && (
              <div className="flex items-center justify-between">
                <span className="theme-text-primary" style={{ opacity: 0.7 }}>Risk to Self:</span>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.9 }}>
                  {client.riskToSelf}
                </span>
              </div>
            )}
            {client.riskToOthers && (
              <div className="flex items-center justify-between">
                <span className="theme-text-primary" style={{ opacity: 0.7 }}>Risk to Others:</span>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.9 }}>
                  {client.riskToOthers}
                </span>
              </div>
            )}
          </div>
        </Box>
      </Box>
      
      {/* Mental Health Assessment */}
      <Box sx={{ mb: 3, p: { xs: 2, sm: 3, md: 4 }, background: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
        <div className="flex justify-between items-center mb-6">
        <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Mental Health Assessment
          </Typography>
        </div>
        <Box sx={{ p: 2, background: 'rgba(255,255,255,0.03)', borderRadius: 1.5 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium theme-text-primary mb-4">Depression (PHQ-9)</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Score:</span>
                <span className="text-sm font-medium theme-text-primary">{client.phq9Score || 'Not assessed'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Severity:</span>
                <span className="text-sm font-medium theme-text-primary">{getDepressionSeverity(client)}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-medium theme-text-primary mb-4">Anxiety (GAD-7)</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Score:</span>
                <span className="text-sm font-medium theme-text-primary">{client.gad7Score || 'Not assessed'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Severity:</span>
                <span className="text-sm font-medium theme-text-primary">{getAnxietySeverity(client)}</span>
              </div>
            </div>
          </div>
        </div>
        </Box>
      </Box>

      {/* General Information */}
      <div className="glass-card-light rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            General Information
          </Typography>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium theme-text-primary mb-4">Presenting Issues</h3>
            <div className="glass-card p-4 rounded-lg">
              <div className="space-y-4">
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>General Concerns:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.generalConcerns || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Duration of Concerns:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.concernsDuration || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>What do you want from counselling?:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.mainObjectives || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Personal Preferences:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.personalPreferences || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium theme-text-primary mb-4">Medical History</h3>
            <div className="glass-card p-4 rounded-lg">
              <div className="space-y-4">
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Medication:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.medication || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Previous Counselling:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.previousCounsellor || 'Not provided'}</p>
                </div>
                <div>
                  <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Official Diagnoses:</span>
                  <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.officialDiagnoses || 'Not provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Substance Use */}
      <div className="glass-card-light rounded-lg p-6">
      <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 0
            }}
          >
            Substance Use
          </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="glass-card p-4 rounded-lg">
            <div className="space-y-4">
              <div>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Recreational Drugs:</span>
                <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.recreationalDrugs || 'Not provided'}</p>
              </div>
              <div>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Alcohol Use:</span>
                <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.alcoholUse || 'Not provided'}</p>
              </div>
              <div>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Substance Support:</span>
                <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.substanceSupport || 'Not provided'}</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-4 rounded-lg">
            <div className="space-y-4">
              <div>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Personality Disorder:</span>
                <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.personalityDisorder || 'Not provided'}</p>
              </div>
              <div>
                <span className="text-sm theme-text-primary" style={{ opacity: 0.7 }}>Other Support:</span>
                <p className="mt-1 text-sm theme-text-primary" style={{ opacity: 0.9 }}>{client.assessment?.otherSupport || 'Not provided'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>




    </div>
  );
};
